<template>
    <div>
        <form-modal
            ref="formModal"
            :title="modalTitle"
            size="lg"
            :in-editing="isEdit"
            :handle-close="handleClose"
        >
            <template v-slot:form>
                <form @submit.prevent="handleSubmit">
                    <div
                        v-if="bandHasId"
                        class="loja-perfeita-modal-toolbar d-flex justify-content-end align-items-center flex-nowrap w-100 mb-2"
                    >
                        <edit-label-button
                            v-if="!isEdit"
                            :title="getI18n(i18nKey, 'TITLES.faixa')"
                            :disabled="submitLoading"
                            @onClick="enterEditMode"
                        />
                        <delete-label-button
                            :title="getI18n(i18nKey, 'TITLES.faixa')"
                            :disabled="submitLoading"
                            @onClick="confirmDeleteBand"
                        />
                    </div>
                    <b-row>
                        <b-col md="12">
                            <b-form-group
                                :label="getI18n(i18nKey, 'TITLES.nome')"
                                label-for="band-label"
                                class="required label-pdv"
                            >
                                <b-form-input
                                    id="band-label"
                                    v-model.trim="formData.label"
                                    class="input-pdv-blue"
                                    type="text"
                                    :disabled="submitLoading || !isFormEditable"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col md="4">
                            <b-form-group
                                :label="getI18n(i18nKey, 'FIELDS.score_min')"
                                label-for="band-min"
                                class="label-pdv"
                            >
                                <b-form-input
                                    id="band-min"
                                    v-model.number="formData.min_score"
                                    class="input-pdv-blue"
                                    type="number"
                                    min="0"
                                    max="100"
                                    :disabled="submitLoading || !isFormEditable"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col md="4">
                            <b-form-group
                                :label="getI18n(i18nKey, 'FIELDS.score_max')"
                                label-for="band-max"
                                class="label-pdv"
                            >
                                <b-form-input
                                    id="band-max"
                                    v-model.number="formData.max_score"
                                    class="input-pdv-blue"
                                    type="number"
                                    min="0"
                                    max="100"
                                    :disabled="submitLoading || !isFormEditable"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col md="4">
                            <b-form-group
                                :label="getI18n(i18nKey, 'FIELDS.color')"
                                label-for="band-color"
                                class="label-pdv"
                            >
                                <b-form-input
                                    id="band-color"
                                    v-model="formData.color"
                                    class="input-pdv-blue"
                                    type="color"
                                    :disabled="submitLoading || !isFormEditable"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <div class="d-flex justify-content-end gap-2 mt-3">
                        <b-button class="btn btn-pdv-gradient-blue-transparent" :disabled="submitLoading" @click="handleClose">
                            {{ getI18n(i18nKey, 'BUTTONS.cancelar') }}
                        </b-button>
                        <b-button
                            class="btn-pdv-gradient-blue ml-3"
                            type="submit"
                            :disabled="submitLoading || !formData.label || formData.min_score === null || formData.min_score === undefined || formData.max_score === null || formData.max_score === undefined || !isFormEditable"
                        >
                            <b-spinner v-if="submitLoading" small class="mr-1" />
                            {{ getI18n(i18nKey, 'BUTTONS.salvar') }}
                        </b-button>
                    </div>
                </form>
            </template>
        </form-modal>

        <b-modal
            ref="deleteConfirmModal"
            centered
            size="md"
            @hidden="deleteConfirmCallback = null"
        >
            <template v-slot:modal-header>
                <h4 class="modal-title description">
                    {{ deleteConfirmTitle }}
                </h4>
                <button type="button" class="close" aria-label="Close" @click="closeDeleteConfirm">
                    <i class="fe-x-circle icon-blue" aria-hidden="true"></i>
                </button>
            </template>
            <div class="message">
                {{ deleteConfirmBody }}
            </div>
            <template v-slot:modal-footer>
                <div class="col text-center ml-3 mr-3">
                    <delete-button
                        class="mb-1"
                        :busy="deleteConfirmBusy"
                        :title="getI18n(i18nKey, 'BUTTONS.excluir')"
                        @onClick="executeDeleteConfirm"
                    />
                    <cancel-label-button @onClick="closeDeleteConfirm" />
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script>
import FormModal from '@src/components/FormModal.vue'
import EditLabelButton from '@/src/components/EditLabelButton.vue'
import DeleteLabelButton from '@/src/components/DeleteLabelButton.vue'
import DeleteButton from '@src/components/DeleteButton.vue'
import CancelLabelButton from '@src/components/CancelLabelButton.vue'
import LojaPerfeitaService from '@src/services/dashboards/LojaPerfeitaService'
import Shared from '@src/mixins/shared.vue'
import swalFeedback from '@src/mixins/swalFeedback'

const i18nKey = 'LOJA_PERFEITA'
const defaultForm = () => ({
    id: 0,
    label: '',
    min_score: 0,
    max_score: 100,
    color: '#6c757d',
})

export default {
    components: {
        FormModal,
        EditLabelButton,
        DeleteLabelButton,
        DeleteButton,
        CancelLabelButton,
    },
    mixins: [Shared, swalFeedback],
    props: { campaignId: { type: [String, Number], required: true } },
    data() {
        return {
            i18nKey,
            isEdit: false,
            submitLoading: false,
            formData: defaultForm(),
            deleteConfirmTitle: '',
            deleteConfirmBody: '',
            deleteConfirmCallback: null,
            deleteConfirmBusy: false,
        }
    },
    computed: {
        bandHasId() {
            return Boolean(this.formData.id && Number(this.formData.id) > 0)
        },
        modalTitle() {
            return this.formData.id
                ? this.getI18n(i18nKey, 'BUTTONS.editar_faixa')
                : this.getI18n(i18nKey, 'BUTTONS.adicionar_faixa')
        },
        isFormEditable() {
            return !this.bandHasId || this.isEdit
        },
    },
    methods: {
        enterEditMode() {
            this.isEdit = true
            this.$nextTick(() => {
                const el = document.getElementById('band-label')
                if (el) el.focus()
            })
        },
        openDeleteConfirm(title, body, callback) {
            this.deleteConfirmTitle = title
            this.deleteConfirmBody = body
            this.deleteConfirmCallback = callback
            this.deleteConfirmBusy = false
            this.$refs.deleteConfirmModal.show()
        },
        closeDeleteConfirm() {
            this.$refs.deleteConfirmModal.hide()
            this.deleteConfirmCallback = null
        },
        executeDeleteConfirm() {
            if (!this.deleteConfirmCallback) return
            this.deleteConfirmBusy = true
            Promise.resolve(this.deleteConfirmCallback())
                .then(() => {
                    this.closeDeleteConfirm()
                })
                .catch((err) => {
                    this.negativeFeedback(this.getI18n('RESPONSES', 'something_wrong'), err?.message)
                })
                .finally(() => {
                    this.deleteConfirmBusy = false
                })
        },
        confirmDeleteBand() {
            if (!this.bandHasId) return
            const id = this.formData.id
            this.openDeleteConfirm(
                this.getI18n(i18nKey, 'MESSAGES.confirm_excluir_faixa_title'),
                this.getI18n(i18nKey, 'MESSAGES.confirm_excluir_faixa'),
                () => LojaPerfeitaService.deletePerformanceBand(id).then(() => {
                    this.successFeedback(this.getI18n(i18nKey, 'TITLES.faixa'), this.getI18n(i18nKey, 'MESSAGES.config_salva'))
                    this.handleClose()
                    this.$emit('refresh')
                })
            )
        },
        showModal() {
            this.submitLoading = false
            this.$refs.formModal.show()
        },
        replaceModalData(data) {
            this.formData = { ...defaultForm(), ...data }
            this.isEdit = !this.bandHasId
        },
        handleClose() {
            this.$refs.formModal.hide()
        },
        handleSubmit() {
            if (!this.formData.label || this.submitLoading || !this.isFormEditable) return
            this.submitLoading = true
            const payload = {
                label: this.formData.label,
                min_score: Number(this.formData.min_score) ?? 0,
                max_score: Number(this.formData.max_score) ?? 100,
                color: this.formData.color || '#6c757d',
            }
            const promise = this.formData.id
                ? LojaPerfeitaService.updatePerformanceBand(this.formData.id, payload)
                : LojaPerfeitaService.createPerformanceBand(this.campaignId, payload)
            promise
                .then(() => {
                    this.successFeedback(this.getI18n(i18nKey, 'TITLES.faixa'), this.getI18n(i18nKey, 'MESSAGES.faixa_salva'))
                    this.handleClose()
                    this.$emit('refresh')
                })
                .catch((err) => {
                    this.negativeFeedback(
                        this.getI18n('RESPONSES', 'something_wrong'),
                        err?.response?.data?.message || err?.message || this.getI18n('RESPONSES', 'operation_not_possible')
                    )
                })
                .finally(() => { this.submitLoading = false })
        },
    },
}
</script>

<style scoped>
.loja-perfeita-modal-toolbar {
    gap: 0.5rem;
    overflow-x: auto;
}
</style>
